import React from 'react';

/**
 * Card — TestCoachAI design system
 *
 * Elevated surface container. Supports optional header/footer slots,
 * interactive hover lift, and dark/accent variants.
 *
 * Variants:    default | flat | elevated | ink | accent
 * interactive: true → hover lift + pointer cursor (use onClick too)
 * header:      ReactNode rendered in .tc-card-header
 * footer:      ReactNode rendered in .tc-card-footer (right-aligned)
 * padding:     false → removes the .tc-card-body wrapper (full-bleed)
 *
 * Usage:
 *   import { Card } from '../ui';
 *
 *   // Simple card
 *   <Card><p>Content here</p></Card>
 *
 *   // With header and footer
 *   <Card header={<h3>Recent Results</h3>} footer={<Button>See all</Button>}>
 *     <p>Content</p>
 *   </Card>
 *
 *   // Interactive / clickable
 *   <Card interactive onClick={() => navigate('/topic/1')}>
 *     <p>Click me</p>
 *   </Card>
 *
 *   // Dark ink panel
 *   <Card variant="ink"><p>Dark content</p></Card>
 */
export function Card({
  variant = 'default',
  interactive = false,
  header = null,
  footer = null,
  padding = true,
  children,
  className = '',
  onClick,
  ...props
}) {
  const cls = [
    'tc-card',
    variant !== 'default' ? `tc-card--${variant}` : '',
    interactive ? 'tc-card--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cls} onClick={onClick} {...props}>
      {header && <div className="tc-card-header">{header}</div>}
      {padding ? (
        <div className="tc-card-body">{children}</div>
      ) : (
        children
      )}
      {footer && <div className="tc-card-footer">{footer}</div>}
    </div>
  );
}
